import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { CluePanel } from '../components/CluePanel';
import { Crossword } from '../components/Crossword';
import type { RejectedSubmission } from '../components/Crossword';
import { Leaderboard } from '../components/Leaderboard';
import { PlayerList } from '../components/PlayerList';
import { WaitingRoom } from '../components/WaitingRoom';
import { WordToast } from '../components/WordToast';
import { useGameState } from '../hooks/useGameState';
import { useWebSocket } from '../hooks/useWebSocket';
import { Results } from './Results';
import { apiJson } from '../api';
import type { ClientSession, CrosswordData, CrosswordWord, ServerMessage } from '../types';

interface GameProps {
  session: ClientSession;
  onLeave: () => void;
}

function formatDuration(seconds: number): string {
  const totalSeconds = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}:${String(remainingMinutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
}

function CompletionBurst() {
  return (
    <div aria-hidden="true" className="confetti-burst">
      {Array.from({ length: 80 }, (_, index) => {
        const style = {
          '--i': index,
          '--left': `${(index * 37) % 100}%`,
          '--delay': `${(index % 16) * 45}ms`,
          '--drift': `${((index * 19) % 90) - 45}px`,
          '--r': `${index * 17}deg`,
        } as CSSProperties;
        return <span key={index} style={style} />;
      })}
    </div>
  );
}

export function Game({ session, onLeave }: GameProps) {
  const [crossword, setCrossword] = useState<CrosswordData | null>(null);
  const [selectedWord, setSelectedWord] = useState<CrosswordWord | null>(null);
  const [draft, setDraft] = useState('');
  const [boardDrafts, setBoardDrafts] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState('');
  const [copied, setCopied] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [rejection, setRejection] = useState<RejectedSubmission | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const topBarRef = useRef<HTMLElement>(null);
  const { snapshot, setSnapshot, applyMessage, lastMessage } = useGameState();

  useEffect(() => {
    if (!menuOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (event.target instanceof Node && !topBarRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [menuOpen]);

  const handleMessage = useCallback(
    (message: ServerMessage) => {
      applyMessage(message);
      if (message.type === 'word_submitted' && message.submitted_by === session.player_id) {
        if (message.is_correct === false && typeof message.word_id === 'string') {
          setRejection({ wordId: message.word_id, nonce: Date.now() });
          setNotice('Incorrect guess.');
        } else {
          setNotice('Word accepted!');
        }
      }
      if (message.type === 'game_started') setNotice('All players ready — game on!');
    },
    [applyMessage, session.player_id]
  );
  const { status, error, send } = useWebSocket(session, handleMessage);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiJson<CrosswordData>('/api/crossword/current').catch(() => null),
      apiJson(`/api/games/${encodeURIComponent(session.game_id)}`),
    ])
      .then(([crosswordData, gameData]) => {
        if (cancelled) return;
        setCrossword(crosswordData);
        setSnapshot(gameData as Parameters<typeof setSnapshot>[0]);
      })
      .catch((err: unknown) => setNotice(err instanceof Error ? err.message : 'Unable to load game'));
    return () => {
      cancelled = true;
    };
  }, [session.game_id, setSnapshot]);

  useEffect(() => {
    if (lastMessage === 'word_partial_points') setNotice('Partial points awarded.');
    if (lastMessage === 'error') setNotice('The server rejected the last action.');
  }, [lastMessage]);

  const currentPlayer = snapshot?.players.find((player) => player.id === session.player_id);
  const gameUrl = session.join_url ?? `${window.location.origin}/?game=${session.game_id}`;
  const solvedCount = Object.values(snapshot?.submitted_answers ?? {}).filter((answer) => answer.is_correct).length;
  const wordsTotal = crossword?.words.length ?? 0;
  const boardSolved = wordsTotal > 0 && solvedCount >= wordsTotal;
  const completionSeconds =
    snapshot?.started_at && snapshot.ended_at
      ? (new Date(snapshot.ended_at).getTime() - new Date(snapshot.started_at).getTime()) / 1000
      : null;
  const showCompletion = Boolean(snapshot?.ended_at && boardSolved && completionSeconds !== null);
  const turnPlayer = snapshot?.current_turn
    ? snapshot.players.find((player) => player.id === snapshot.current_turn?.player_id)
    : null;

  const statusText = useMemo(() => {
    if (!snapshot) return 'Loading game';
    if (snapshot.ended_at) return 'Game complete';
    if (snapshot.phase === 'waiting') return 'Waiting for players';
    if (snapshot.play_type === 'turn-based') {
      return turnPlayer ? `${turnPlayer.name}'s turn` : 'Waiting for turn order';
    }
    return snapshot.mode === 'cooperative' ? 'Solve together' : 'Play at your own pace';
  }, [snapshot, turnPlayer]);

  const selectWord = (word: CrosswordWord) => {
    setSelectedWord(word);
    setDraft('');
  };

  const deselectWord = () => {
    setSelectedWord(null);
    setDraft('');
  };

  const submitAnswer = () => {
    if (snapshot?.answer_check_mode === 'advanced') {
      setNotice('Keep filling the board, then use I’m Done to check it.');
      return;
    }
    if (!selectedWord) return;
    const letters = draft.replace(/[^a-z]/gi, '');
    if (letters.length < selectedWord.length) {
      setNotice('Fill every letter before submitting.');
      return;
    }
    const sent = send({ type: 'word_submission', word_id: selectedWord.id, answer: draft });
    if (sent) setNotice('Answer sent.');
  };

  const markDone = () => {
    if (snapshot?.answer_check_mode === 'advanced') {
      send({ type: 'done', answers: boardDrafts });
      return;
    }
    if (window.confirm("Mark yourself as done? You can still view but won't earn more points.")) {
      send({ type: 'done' });
    }
  };

  const copyJoinUrl = async () => {
    await navigator.clipboard.writeText(gameUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  if (!snapshot) {
    return (
      <main className="game-page loading-page">
        <p>{notice || 'Loading game...'}</p>
        <button onClick={onLeave} type="button">Back</button>
      </main>
    );
  }

  if (showResults) {
    return <Results snapshot={snapshot} onBack={() => setShowResults(false)} />;
  }

  const waiting = snapshot.phase === 'waiting' && !snapshot.ended_at;
  const showDoneButton =
    !waiting &&
    (snapshot.answer_check_mode === 'advanced' ||
      (snapshot.mode === 'competitive' && snapshot.players.length > 1));
  const showPlayerList = snapshot.players.length > 1;

  return (
    <main className="game-page">
      <div aria-hidden="true" className="orientation-overlay">
        <p>Game best played in portrait mode</p>
      </div>

      <header className="top-bar" ref={topBarRef}>
        <button className="ghost-button" onClick={onLeave} type="button">Back</button>
        <div className="top-bar-title">
          <strong>{statusText}</strong>
          <small>{notice || error || `${status} connection`}</small>
        </div>
        <div className="top-bar-meta">
          <button
            aria-expanded={menuOpen}
            aria-label={menuOpen ? 'Close game settings' : 'Open game settings'}
            className="hamburger-button"
            onClick={() => setMenuOpen((open) => !open)}
            type="button"
          >
            <span className="hamburger-line" />
            <span className="hamburger-line" />
            <span className="hamburger-line" />
          </button>
          <div className={`top-bar-settings ${menuOpen ? 'open' : ''}`}>
            <div className="game-settings-chips" title="Game settings are locked for this game">
              <span>{snapshot.mode === 'competitive' ? 'Competitive' : 'Cooperative'}</span>
              <span>{snapshot.play_type === 'turn-based' ? 'Turn-based' : 'Free-for-all'}</span>
              <span>{snapshot.answer_check_mode === 'advanced' ? 'Advanced' : 'Easy'}</span>
              {snapshot.play_type === 'turn-based' && snapshot.turn_limit_seconds && (
                <span>{snapshot.turn_limit_seconds}s turns</span>
              )}
            </div>
            <button className="join-code" onClick={copyJoinUrl} type="button">
              {copied ? 'Copied' : snapshot.game_id.split('-').at(-1)?.toUpperCase() ?? 'Copy'}
            </button>
          </div>
        </div>
      </header>

      <WordToast filledLetters={draft.length} onDismiss={deselectWord} word={selectedWord} />
      {selectedWord && <div aria-hidden="true" className="word-toast-spacer" />}

      {showCompletion && (
        <section className="completion-banner" aria-live="polite">
          <CompletionBurst />
          <strong>Crossword complete</strong>
          <span>Total time {formatDuration(completionSeconds ?? 0)}</span>
        </section>
      )}

      {waiting ? (
        <WaitingRoom
          currentPlayerId={session.player_id}
          onReady={() => send({ type: 'ready' })}
          onStartSolo={() => send({ type: 'start_solo' })}
          snapshot={snapshot}
        />
      ) : (
        <section className="game-layout">
          <div className="board-column">
            {crossword ? (
              <Crossword
                crossword={crossword}
                onDeselectWord={deselectWord}
                onDraftChange={setDraft}
                onDraftsChange={setBoardDrafts}
                onSelectWord={selectWord}
                onSubmit={submitAnswer}
                rejection={rejection}
                selectedWordId={selectedWord?.id ?? null}
                submittedAnswers={snapshot.submitted_answers}
              />
            ) : (
              <section className="empty-state">
                <h2>Crossword unavailable</h2>
                <p>This week’s puzzle is not loaded yet. The game room is ready once R2 has a puzzle.</p>
              </section>
            )}
          </div>

          <aside className="side-column">
            {crossword && (
              <CluePanel
                mode={snapshot.mode}
                onSelectWord={selectWord}
                selectedWordId={selectedWord?.id ?? null}
                submittedAnswers={snapshot.submitted_answers}
                words={crossword.words}
              />
            )}
            {showPlayerList && <PlayerList players={snapshot.players} currentPlayerId={session.player_id} />}
            {snapshot.ended_at && (
              <Leaderboard
                endedAt={snapshot.ended_at}
                mode={snapshot.mode}
                players={snapshot.players}
                startedAt={snapshot.started_at}
              />
            )}
          </aside>
        </section>
      )}

      <footer className="bottom-bar">
        {showDoneButton && (
          <button disabled={currentPlayer?.status === 'done'} onClick={markDone} type="button">
            I'm Done
          </button>
        )}
        {snapshot.ended_at && (
          <button onClick={() => setShowResults(true)} type="button">
            Results
          </button>
        )}
        <small>v1.1.0</small>
      </footer>
    </main>
  );
}
